<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import Button from '@components/Button.vue';

interface LocationType {
    id: number;
    title: string;
}

const props = withDefaults(
    defineProps<{
        locations?: number[] | null;
        locationTypes?: LocationType[] | null;
        showFilter?: boolean;
        showApplicationDetails?: boolean;
    }>(),
    {
        locations: null,
        locationTypes: null,
        showFilter: false,
        showApplicationDetails: false,
    }
);

const instance = ref<LocationsMap | null>(null);
const container = ref<HTMLElement | null>(null);
const popup = ref<HTMLElement | null>(null);
const activeFilter = ref<number | null>(null);
const activeCluster = ref<Cluster | null>(null);

const onFilterClick = (locationTypeId: number | null) => {
    if (activeFilter.value === locationTypeId || locationTypeId === null) {
        activeFilter.value = null;
        instance.value?.drawMarkers();
        return;
    }

    activeFilter.value = locationTypeId;
    instance.value?.filterByType(locationTypeId);
};

onMounted(async () => {
    const [baseData, locationData] = await Promise.all([
        fetch('/assets/niedersachsen.json'),
        fetch(
            `/api/locations.geojson?applicationDetails=${props.showApplicationDetails ? '1' : '0'}`
        ),
    ])
        .then(async (responses) => {
            return await Promise.all(
                responses.map((response) => response.json())
            );
        })
        .catch((err) => {
            console.error('Could not fetch locations:', err);
            return [null, null];
        });

    instance.value = new LocationsMap({
        container: container.value as HTMLElement,
        baseData,
        locationData,
        initialLocations: props.locations,
        onClick: (cluster) => {
            activeCluster.value = cluster;
        },
    });
});

const onClick = (e: PointerEvent) => {
    const { target } = e;

    if (
        target?.__data__?.id === activeCluster.value?.id ||
        popup.value?.contains(target)
    ) {
        return;
    }

    activeCluster.value = null;
    instance.value?.reset();
};

const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        activeCluster.value = null;
        instance.value?.reset();
    }
};

watch(activeCluster, (value) => {
    if (value === null) {
        window.removeEventListener('click', onClick);
        window.removeEventListener('keydown', onKeyDown);
        return;
    }

    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKeyDown);
});
</script>

<script lang="ts">
import { toRaw } from 'vue';
import PinIcon from '@assets/icons/pin.svg?raw';
import * as d3 from 'd3';
import type { Feature, FeatureCollection, Geometry, Point } from 'geojson';
import * as topojson from 'topojson-client';
import type {
    GeometryCollection,
    Topology,
    Transform,
} from 'topojson-specification';

const WIDTH = 1200;
const HEIGHT = 1040;

const MARKER_WIDTH = 42;
const MARKER_HEIGHT = 56;

const CLUSTER_RADIUS = 35;

interface CountiesTopo extends Topology {
    objects: {
        counties: GeometryCollection;
    };
    bbox: [number, number, number, number];
    transform: Transform;
}

interface LocationData {
    id: number;
    title: string;
    address: string;
    typeId: number[];
    contactWays?: {
        type: 'telephone' | 'fax' | 'email' | 'website';
        value: string;
    }[];
    applicationDetails: string | null;
}

interface Cluster {
    id: string;
    x: number;
    y: number;
    features: Feature<Point, LocationData>[];
    centroid: [number, number];
    showPopup: boolean;
}

type Features = Feature<Point, LocationData>[];

class LocationsMap {
    private readonly projection: d3.GeoProjection;
    private readonly svg;
    private g: d3.Selection<SVGGElement, any, null, undefined>;
    private markers: d3.Selection<SVGGElement, any, null, undefined>;
    private countyPaths: d3.Selection<
        SVGPathElement | d3.BaseType,
        Feature<Geometry, any>,
        SVGGElement,
        any
    >;
    private readonly features: Features;
    private counties: Feature<Geometry, any>[];
    private readonly initialLocations: number[];

    private readonly onClick: Function;

    constructor({
        container,
        baseData,
        locationData,
        initialLocations,
        onClick,
    }: {
        container: HTMLElement;
        baseData: CountiesTopo;
        locationData: FeatureCollection<Point, LocationData>;
        initialLocations: number[] | null;
        onClick: (cluster: Cluster) => void;
    }) {
        this.svg = d3
            .create('svg')
            .attr('viewBox', [0, 0, WIDTH, HEIGHT])
            .attr('style', `aspect-ratio: ${WIDTH} / ${HEIGHT}`)
            .attr('class', 'overflow-visible mb-5 sm:mb-8');

        this.g = this.svg.append('g');

        this.svg
            .append('defs')
            .html(PinIcon)
            .select('svg')
            .attr('id', 'marker');

        // Convert topojson to geojson
        const geojson = topojson.feature(baseData, baseData.objects.counties);
        this.counties = geojson.features;

        // Create a projection that fits the data to the SVG dimensions
        this.projection = d3.geoMercator().fitSize([WIDTH, HEIGHT], geojson);

        const path = d3.geoPath(this.projection);

        this.countyPaths = this.g
            .append('g')
            .attr('fill', '#1E1E1E77')
            .attr('stroke', '#FFF')
            .attr('stroke-width', 2)
            .selectAll('path')
            .data(geojson.features)
            .join('path')
            .attr('d', path);

        this.markers = this.g.append('g');

        this.features = locationData.features;

        if (initialLocations !== null) {
            this.initialLocations = initialLocations;
        }

        this.onClick = onClick;

        this.drawMarkers();

        container.prepend(this.svg.node() as SVGSVGElement);
    }

    createClusters(features: Features) {
        const clusters: Cluster[] = [];
        const processed = new Set<Feature<Point, LocationData>>();

        features.forEach((feature) => {
            if (processed.has(feature)) return;

            const position = this.projection(
                feature.geometry.coordinates as [number, number]
            ) as [number, number];

            const cluster: Cluster = {
                id: `cluster-${clusters.length}`,
                x: position[0],
                y: position[1],
                features: [feature],
                centroid: feature.geometry.coordinates as [number, number],
                showPopup: false,
            };

            processed.add(feature);

            // Find nearby features to add to this cluster
            features.forEach((otherFeature) => {
                if (processed.has(otherFeature)) return;

                const otherPosition = this.projection(
                    otherFeature.geometry.coordinates as [number, number]
                ) as [number, number];

                const distance = Math.sqrt(
                    Math.pow(position[0] - otherPosition[0], 2) +
                        Math.pow(position[1] - otherPosition[1], 2)
                );

                if (distance <= CLUSTER_RADIUS) {
                    cluster.features.push(otherFeature);
                    processed.add(otherFeature);
                }
            });

            // Calculate cluster centroid
            if (cluster.features.length > 1) {
                const avgLng =
                    cluster.features.reduce((sum, f) => {
                        const coords = f.geometry.coordinates as [
                            number,
                            number,
                        ];
                        return sum + Number(coords[0]);
                    }, 0) / cluster.features.length;

                const avgLat =
                    cluster.features.reduce((sum, f) => {
                        const coords = f.geometry.coordinates as [
                            number,
                            number,
                        ];
                        return sum + Number(coords[1]);
                    }, 0) / cluster.features.length;

                cluster.centroid = [avgLng, avgLat];

                const centroidPosition = this.projection(cluster.centroid) as [
                    number,
                    number,
                ];
                cluster.x = centroidPosition[0];
                cluster.y = centroidPosition[1];
            }

            clusters.push(cluster);
        });

        return clusters;
    }

    drawMarkers(features: Features | null = null) {
        if (!features) {
            features = this.getFeatures();
        }

        const clusters = this.createClusters(features);

        this.markers
            .selectAll('use')
            .data(clusters)
            .join(
                (enter) => enter.append('use'),
                (update) => update,
                (exit) => exit.remove()
            )
            .attr('href', '#marker')
            .attr(
                'transform',
                (d) =>
                    `translate(${d.x - MARKER_WIDTH * 0.5} ${d.y - MARKER_HEIGHT})`
            )
            .attr('class', 'cursor-pointer focus:outline-0')
            .on('click', (e: PointerEvent, d: Cluster) => {
                if (!d.features[0]) {
                    return;
                }

                this.highlightCounty(
                    d.features[0].geometry.coordinates as [number, number]
                );
                this.onClick(d);
            });
    }

    highlightCounty(coordinates: [number, number]) {
        let containingCounty = this.counties.find((county) =>
            d3.geoContains(county, coordinates)
        );

        this.reset();

        if (!containingCounty) return;

        // not sure why this becomes a proxy object?!
        containingCounty = toRaw(containingCounty);

        // Find the corresponding path element and style it
        this.countyPaths
            .filter((pathData) => pathData === containingCounty)
            .attr('fill', '#1E1E1E');
    }

    reset() {
        this.countyPaths.attr('fill', null);
    }

    getFeatures() {
        if (!this.initialLocations) {
            return this.features;
        }

        return this.features.filter((feature) =>
            this.initialLocations.includes(feature.properties.id)
        );
    }

    filterByType(locationTypeId: number) {
        const filteredFeatures = this.features.filter((feature) =>
            feature.properties.typeId.includes(locationTypeId)
        );

        this.drawMarkers(filteredFeatures);
    }
}
</script>

<template>
    <div
        v-if="showFilter && locationTypes && locationTypes.length > 1"
        class="mb-10 flex flex-wrap justify-start gap-2 sm:justify-center sm:gap-4 md:mb-16 lg:mb-20"
    >
        <Button
            :variant="activeFilter === null ? 'default' : 'outline'"
            size="xs"
            @click="() => onFilterClick(null)"
        >
            {{ $t('Ordinary Jurisdiction') }}
        </Button>

        <Button
            v-for="locationType in locationTypes"
            :key="locationType.id"
            size="xs"
            :variant="activeFilter === locationType.id ? 'default' : 'outline'"
            @click="() => onFilterClick(locationType.id)"
        >
            {{ locationType.title }}
        </Button>

        <Button
            :variant="activeFilter === 0 ? 'default' : 'outline'"
            size="xs"
            @click="() => onFilterClick(0)"
        >
            ZIB
        </Button>
    </div>
    <div
        ref="container"
        class="relative mx-auto mb-16 max-w-[720px] md:mb-32"
    >
        <div
            v-if="showFilter && activeFilter === 0"
            class="bg-primary text-primary-foreground mx-auto w-full overflow-y-auto rounded-b border-2 border-current px-5 pb-4 *:py-6 sm:w-sm md:w-md lg:absolute lg:top-1/2 lg:left-1/2 lg:max-h-[50vh] lg:w-lg lg:w-min lg:max-w-[80vw] lg:-translate-x-1/2 lg:-translate-y-1/2 lg:px-10 xl:mx-0"
        >
            <p class="mb-4 text-base font-bold uppercase">
                Zentraler IT-Betrieb<br />
                Niedersächsische Justiz
            </p>
            <div class="space-y-8 text-sm leading-normal">
                <slot name="zib" />
            </div>
        </div>
        <div
            v-if="activeCluster"
            ref="popup"
            :key="activeCluster.id"
            class="bg-primary text-primary-foreground mx-auto w-full divide-y-2 overflow-y-auto rounded-b border-2 border-current px-5 pb-4 *:py-6 sm:w-sm md:w-lg lg:w-lg lg:px-10 xl:absolute xl:top-0 xl:left-0 xl:mx-0 xl:max-h-[50vh] xl:w-min xl:-translate-x-1/2 xl:-translate-y-full"
            :style="{
                left: activeCluster.x + 'px',
                top: activeCluster.y - MARKER_HEIGHT * 0.6 + 'px',
            }"
        >
            <component
                v-for="feature in activeCluster.features"
                :key="feature.id"
                :is="activeCluster.features.length === 1 ? 'div' : 'details'"
                class="group/detail max-w-full overflow-hidden"
            >
                <p
                    v-if="activeCluster.features.length === 1"
                    class="font-bold uppercase"
                >
                    {{ feature.properties.title }}
                </p>
                <summary
                    v-else
                    class="focus-visible:ring-ring flex cursor-pointer list-none items-start justify-between gap-4 font-bold uppercase marker:hidden focus:outline-0 focus-visible:ring-2 focus-visible:ring-inset"
                >
                    <span> {{ feature.properties.title }}</span>
                    <svg
                        class="translate mt-1 h-6 w-6 shrink-0 rotate-0 transition transition-transform duration-200 group-open/detail:rotate-180"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </summary>

                <div
                    v-if="
                        showApplicationDetails &&
                        feature.properties.applicationDetails !== null
                    "
                    class="mt-8 space-y-8 text-sm leading-normal whitespace-pre-wrap"
                    v-html="feature.properties.applicationDetails"
                ></div>

                <div
                    v-else
                    class="mt-8 flex flex-col gap-y-8"
                >
                    <p class="text-sm leading-normal whitespace-pre-wrap">
                        {{ feature.properties.address }}
                    </p>
                    <div class="text-sm leading-normal font-bold">
                        <p
                            v-for="contactWay in feature.properties.contactWays"
                            class="text-pretty break-all"
                        >
                            <span v-if="contactWay.type === 'fax'">
                                F:
                                <a
                                    :href="'tel:' + contactWay.value"
                                    class="underline hover:no-underline"
                                    >{{ contactWay.value }}</a
                                >
                            </span>
                            <span v-if="contactWay.type === 'telephone'">
                                T:
                                <a
                                    :href="'tel:' + contactWay.value"
                                    class="underline hover:no-underline"
                                    >{{ contactWay.value }}</a
                                >
                            </span>
                            <span v-if="contactWay.type === 'email'">
                                <a
                                    :href="'mailto:' + contactWay.value"
                                    class="underline hover:no-underline"
                                    >{{ contactWay.value }}</a
                                >
                            </span>
                            <span
                                v-if="contactWay.type === 'website'"
                                class="underline hover:no-underline"
                            >
                                <a
                                    :href="contactWay.value"
                                    target="_blank"
                                    >Zur Webseite</a
                                >
                            </span>
                        </p>
                    </div>
                </div>
            </component>
        </div>
    </div>
</template>
